// SendProdCommands.tsx
import React, { useEffect } from 'react';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import { Box, Button } from '@mui/material';
import {
  useExecuteMultipleProdCommandsMutation,
  useSendCommandBitfieldMutation,
  useSendCommandRepeatSettingMutation,
  useSendDirectMethodMutation,
  useSendEventRepeatSettingMutation,
  useSendMSendSettingMutation
} from '../../../services/device/deviceService';
import {
  StyledContainerFooterSideBar,
  StyledContainerItems,
  StyledTitleSideBar
} from '../../../components/commons/SideBarComponents/sideBarComponentsStyles';
import { CommandItem } from '../../../components/commandItem/commandItem';
import { CommandType } from '../../../constants/enums/commandsDecives';
import Strings from '../../../i18n';

interface SendDirectMethodProps {
  deviceIds: string[];
  setOpenRightSideBar: (value: boolean) => void;
  clearSelectedDevices: () => void;
}

type CommandsForm = {
  msSend: number;
  eventRepeat: number;
  commandRepeat: number;
  bitField: boolean;
  ntpInterval: number;
  lightInterval: number;
  statusInterval: number;
  communicationDelay: number;
};

export const SendProdCommands = ({
  deviceIds,
  setOpenRightSideBar,
  clearSelectedDevices
}: SendDirectMethodProps) => {
  const [sendMs, { isSuccess: isSuccessSendMs, isLoading: isLoadingSendMs }] =
    useSendMSendSettingMutation();
  const [
    sendCommandBitfield,
    { isSuccess: isSuccessCommandBitfield, isLoading: isLoadingCommandBitfield }
  ] = useSendCommandBitfieldMutation();
  const [
    executeMultipleCommands,
    {
      isSuccess: isSuccesseExecuteMultipleCommands,
      isLoading: isLoadingExecuteMultipleCommands
    }
  ] = useExecuteMultipleProdCommandsMutation();

  useEffect(() => {
    if (
      isSuccessSendMs ||
      isSuccessCommandBitfield ||
      isSuccesseExecuteMultipleCommands
    ) {
      toast.success(Strings.devices.productMethodSentSuccess);
    }
  }, [
    isSuccessSendMs,

    isSuccessCommandBitfield,
    isSuccesseExecuteMultipleCommands
  ]);

  const initialValues: CommandsForm = {
    msSend: 16,
    eventRepeat: 4,
    commandRepeat: 1,
    bitField: true,
    ntpInterval: 300,
    lightInterval: 600,
    statusInterval: 600,
    communicationDelay: 0
  };

  const handleSendMs = (value: number) =>
    sendMs({ deviceIds, mSendValue: value });

  const handleCommandBitfield = (value: boolean) =>
    sendCommandBitfield({ deviceIds, bitFieldEnable: value });

  const handleSendAll = (values: CommandsForm) => {
    const multi = [
      {
        CommandType: CommandType.NtpSyncInterval,
        NtpInterval: values.ntpInterval,
        MacAddresses: deviceIds
      },
      {
        CommandType: CommandType.LightSyncInterval,
        LightNtpInterval: values.lightInterval,
        MacAddresses: deviceIds
      },
      {
        CommandType: CommandType.StatusInterval,
        StatusInterval: values.statusInterval,
        MacAddresses: deviceIds
      },
      {
        CommandType: CommandType.CommandRepeat,
        commandRepeat: values.commandRepeat,
        MacAddresses: deviceIds
      },
      {
        CommandType: CommandType.EventRepeat,
        eventRepeat: values.eventRepeat,
        MacAddresses: deviceIds
      },
      {
        CommandType: CommandType.EnableBitfield,
        enableBitfield: values.bitField,
        MacAddresses: deviceIds
      },
      {
        CommandType: CommandType.MSend,
        mSendValue: values.msSend,
        MacAddresses: deviceIds
      },
      {
        CommandType: CommandType.SetCommunicationDelay,
        CommunicationDelay: values.communicationDelay,
        MacAddresses: deviceIds
      }
    ];
    executeMultipleCommands({ commands: multi });
  };

  return (
    <Formik initialValues={initialValues} onSubmit={() => undefined}>
      {(formik) => (
        <Box>
          <StyledContainerItems>
            <StyledTitleSideBar>
              {Strings.serverAccess.devices.sendProductionMethodLabel}
            </StyledTitleSideBar>

            <CommandItem
              nameCommand={Strings.serverAccess.devices.msSend}
              loading={isLoadingSendMs}
              value={formik.values.msSend}
              onChange={(v) => formik.setFieldValue('msSend', v)}
              onSave={(v) => handleSendMs(v)}
            />

            <CommandItem
              nameCommand={Strings.serverAccess.devices.eventRepeat}
              loading={isLoadingExecuteMultipleCommands}
              value={formik.values.eventRepeat}
              onChange={(v) => formik.setFieldValue('eventRepeat', v)}
              onSave={(v) =>
                executeMultipleCommands({
                  commands: [
                    {
                      CommandType: CommandType.EventRepeat,
                      EventRepeat: v,
                      MacAddresses: deviceIds
                    }
                  ]
                })
              }
            />

            <CommandItem
              nameCommand={Strings.serverAccess.devices.commandRepeat}
              loading={isLoadingExecuteMultipleCommands}
              value={formik.values.commandRepeat}
              onChange={(v) => formik.setFieldValue('commandRepeat', v)}
              onSave={(v) =>
                executeMultipleCommands({
                  commands: [
                    {
                      CommandType: CommandType.CommandRepeat,
                      CommandRepeat: v,
                      MacAddresses: deviceIds
                    }
                  ]
                })
              }
            />

            <CommandItem
              nameCommand={Strings.serverAccess.devices.bitField}
              loading={isLoadingCommandBitfield}
              haveGroupButton
              value={formik.values.bitField}
              onChange={(v) => formik.setFieldValue('bitField', v)}
              onSave={(v) => handleCommandBitfield(!!v)}
            />

            <CommandItem
              nameCommand={Strings.serverAccess.devices.syncInterval}
              loading={isLoadingExecuteMultipleCommands}
              value={formik.values.ntpInterval}
              onChange={(v) => formik.setFieldValue('ntpInterval', v)}
              onSave={(v) =>
                executeMultipleCommands({
                  commands: [
                    {
                      CommandType: CommandType.NtpSyncInterval,
                      NtpInterval: v,
                      MacAddresses: deviceIds
                    }
                  ]
                })
              }
            />

            <CommandItem
              nameCommand={Strings.serverAccess.devices.lightInterval}
              loading={isLoadingExecuteMultipleCommands}
              value={formik.values.lightInterval}
              onChange={(v) => formik.setFieldValue('lightInterval', v)}
              onSave={(v) =>
                executeMultipleCommands({
                  commands: [
                    {
                      CommandType: CommandType.LightSyncInterval,
                      LightNtpInterval: v,
                      MacAddresses: deviceIds
                    }
                  ]
                })
              }
            />

            <CommandItem
              nameCommand={Strings.serverAccess.devices.statusMessage}
              loading={isLoadingExecuteMultipleCommands}
              value={formik.values.statusInterval}
              onChange={(v) => formik.setFieldValue('statusInterval', v)}
              onSave={(v) =>
                executeMultipleCommands({
                  commands: [
                    {
                      CommandType: CommandType.StatusInterval,
                      StatusInterval: v,
                      MacAddresses: deviceIds
                    }
                  ]
                })
              }
            />

            <CommandItem
              nameCommand={Strings.serverAccess.devices.communicationDelay}
              loading={isLoadingExecuteMultipleCommands}
              value={formik.values.communicationDelay}
              onChange={(v) => formik.setFieldValue('communicationDelay', v)}
              onSave={(v) =>
                executeMultipleCommands({
                  commands: [
                    {
                      CommandType: CommandType.SetCommunicationDelay,
                      CommunicationDelay: v,
                      MacAddresses: deviceIds
                    }
                  ]
                })
              }
            />
            <Button
              sx={{ mt: 2 }}
              variant="contained"
              onClick={() => handleSendAll(formik.values)}
              disabled={
                isLoadingSendMs ||
                isLoadingCommandBitfield ||
                isLoadingExecuteMultipleCommands
              }
            >
              {Strings.common.sendAllProduction}
            </Button>
          </StyledContainerItems>

          <StyledContainerFooterSideBar>
            <button
              type="button"
              className="btn-cancel"
              onClick={() => {
                setOpenRightSideBar(false);
                clearSelectedDevices();
              }}
            >
              {Strings.common.cancel}
            </button>
          </StyledContainerFooterSideBar>
        </Box>
      )}
    </Formik>
  );
};
